import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import AiCardBase from './AiCardBase';

const GenericAiCard = ({ card, onConfirm, isConfirmed, isConfirming }) => {
  const { cardType, label, data = {} } = card;
  const [formData, setFormData] = useState(data);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const handleFieldChange = (key, val) => {
    setFormData((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  const handleConfirm = () => {
    onConfirm(cardType, formData);
  };

  // Convert data object to editable key-value rows
  const renderFields = () => {
    if (typeof formData !== 'object' || formData === null) {
      return (
        <TextField
          fullWidth
          multiline
          rows={3}
          disabled={isConfirmed}
          value={String(formData || '')}
          onChange={(e) => setFormData(e.target.value)}
          size="small"
        />
      );
    }

    if (Array.isArray(formData)) {
      return (
        <Stack spacing={1}>
          {formData.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                p: 1.25,
                bgcolor: '#F8FAFC',
                borderRadius: 2,
                border: '1px solid #E2E8F0',
              }}
            >
              {typeof item === 'object' ? (
                Object.entries(item).map(([k, v]) => (
                  <Stack direction="row" spacing={1} key={k} mb={0.5}>
                    <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 100 }}>
                      {k}:
                    </Typography>
                    <Typography variant="caption">{String(v)}</Typography>
                  </Stack>
                ))
              ) : (
                <Typography variant="body2">{String(item)}</Typography>
              )}
            </Box>
          ))}
        </Stack>
      );
    }

    const entries = Object.entries(formData);

    if (entries.length === 0) {
      return (
        <Typography variant="body2" sx={{ color: '#94A3B8', fontStyle: 'italic' }}>
          No specific details staged.
        </Typography>
      );
    }

    return (
      <Grid container spacing={2}>
        {entries.map(([key, val]) => {
          // Skip internal IDs
          if (key.toLowerCase().endsWith('id') && key !== 'questionId') return null;

          const isComplex = typeof val === 'object' && val !== null;
          const displayLabel = key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase());

          return (
            <Grid size={{ xs: 12, sm: isComplex ? 12 : 6 }} key={key}>
              <TextField
                fullWidth
                size="small"
                disabled={isConfirmed}
                label={displayLabel}
                multiline={isComplex || String(val).length > 50}
                rows={isComplex || String(val).length > 50 ? 3 : 1}
                value={isComplex ? JSON.stringify(val, null, 2) : (val ?? '')}
                onChange={(e) => {
                  if (isComplex) {
                    try {
                      handleFieldChange(key, JSON.parse(e.target.value));
                    } catch (err) {
                      // Allow typing in JSON string without crashing
                    }
                  } else {
                    handleFieldChange(key, e.target.value);
                  }
                }}
                sx={{ '& input, & textarea': { fontSize: 13 } }}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <AiCardBase
      cardType={cardType}
      label={label || cardType}
      isConfirmed={isConfirmed}
      isConfirming={isConfirming}
      onConfirm={handleConfirm}
      isUpdatedByAi={card.isUpdatedByAi}
    >
      <Box sx={{ py: 1 }}>{renderFields()}</Box>
    </AiCardBase>
  );
};

export default GenericAiCard;
